// Die Übersicht: was ein Plattform-Admin morgens zuerst wissen will.
//
// Oben vier Kennzahlen (Mandanten, Benutzer, Kosten im Monat, Host), darunter
// links „Braucht Aufmerksamkeit" und die Mandanten auf einen Blick, rechts der
// Host, die letzten Läufe und die letzte Aktivität. Alles aus einer Antwort
// (`GET /api/dashboard`).
//
// **Zuerst zeichnen, dann frisch laden** (stale-while-revalidate): Die letzte
// Antwort liegt im Zwischenspeicher (dashboard-cache.ts); die Seite steht damit
// ohne Wartezeit, und die frische Antwort ersetzt sie, sobald sie da ist. Die
// Instanzen fragt nicht die Seite, sondern die Plattform alle fünf Minuten
// (refresh.ts) — „Aktualisieren" stößt denselben Lauf sofort an. Fehlen die
// Host-Werte noch (Plattform gerade gestartet), lädt die Host-Karte für sich
// nach.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { get, post } from "../../api/client";
import { keys } from "../../api/queries";
import type { Dashboard, HostCapacity } from "../../api/types";
import { PageHead } from "../../app/page-head";
import { errorText } from "../../lib/labels";
import { Link } from "../../router/router";
import { ErrorNote, Loading } from "../../ui/badge";
import { toast } from "../../ui/toaster";
import { ActivityCard, AttentionCard, HostCard, Kpis, RunsCard, TenantsCard } from "./dashboard-cards";
import { readDashboardCache, writeDashboardCache } from "./dashboard-cache";
import { standText } from "./dashboard-model";

export function DashboardView() {
  const client = useQueryClient();
  // Der Zwischenspeicher gilt als veraltet (Zeitstempel 0): gezeichnet wird
  // sofort, gefragt aber trotzdem.
  const query = useQuery({
    queryKey: keys.dashboard,
    queryFn: async () => {
      const fresh = await get<Dashboard>("/dashboard");
      writeDashboardCache(fresh);
      return fresh;
    },
    initialData: () => readDashboardCache() ?? undefined,
    initialDataUpdatedAt: 0,
  });
  const data = query.data;
  const fromCache = Boolean(data) && query.dataUpdatedAt === 0 && !query.isError;

  /** Die Host-Karte für sich — nur, wenn die frische Antwort noch keine Werte hat. */
  const lazyHost = useMutation({
    mutationFn: async () => {
      try { return (await get<{ host: HostCapacity | null }>("/host?wait=1")).host; } catch { return null; /* dann „nicht erreichbar" */ }
    },
    onSuccess: (host) => {
      if (!host) return;
      const cur = client.getQueryData<Dashboard>(keys.dashboard);
      if (!cur) return;
      const next = { ...cur, host, host_measured_at: new Date().toISOString() };
      writeDashboardCache(next);
      client.setQueryData(keys.dashboard, next);
    },
  });
  const { mutate: fetchHost, isPending: hostPending } = lazyHost;
  const freshAt = query.dataUpdatedAt;
  const hasHost = Boolean(data?.host);
  // Nach jeder frischen Antwort ohne Host einmal nachfragen; der Merker hält
  // denselben Stand davon ab, es zweimal zu tun.
  const askedFor = useRef(0);
  useEffect(() => {
    if (freshAt === 0 || hasHost || askedFor.current === freshAt) return;
    askedFor.current = freshAt;
    fetchHost();
  }, [freshAt, hasHost, fetchHost]);

  // Ein Fehler im Hintergrund, während der alte Stand dasteht: eine Meldung,
  // die Seite bleibt.
  const failedAt = query.errorUpdatedAt;
  const hadData = Boolean(data);
  const failure = query.error;
  useEffect(() => {
    if (failedAt && hadData && failure) toast(errorText(failure), "error");
  }, [failedAt, hadData, failure]);

  const refresh = useMutation({
    mutationFn: () => post("/tenants/refresh"),
    onError: (err) => toast(errorText(err), "error"),
    onSettled: () => { void query.refetch(); },
  });

  const head = (
    <PageHead
      title="Übersicht"
      hint={data ? standText(data, { refreshing: refresh.isPending, fromCache }) : undefined}
      actions={(
        <>
          <button type="button" className="pf-btn" disabled={refresh.isPending} onClick={() => refresh.mutate()}>Aktualisieren</button>
          <Link className="pf-btn pf-btn--primary" to="/tenants/new">Neuer Mandant</Link>
        </>
      )}
    />
  );

  if (!data) {
    return <>{head}{query.isError ? <ErrorNote error={query.error} /> : <Loading />}</>;
  }
  return (
    <>
      {head}
      <Kpis data={data} hostPending={hostPending} />
      <div className="pf-dash">
        <div className="pf-dash__col"><AttentionCard data={data} /><TenantsCard data={data} /></div>
        <div className="pf-dash__col"><HostCard data={data} pending={hostPending} /><RunsCard data={data} /><ActivityCard data={data} /></div>
      </div>
    </>
  );
}
